import { useEffect, useState } from 'react';
import {
  apiCollectionProductIds,
  apiCreateCollection,
  apiCreateDiscount,
  apiDeleteCollection,
  apiDeleteDiscount,
  apiListCollections,
  apiListDiscounts,
  apiListProducts,
  apiUpdateCollection,
  apiUpdateDiscount,
  type Collection,
  type Discount,
  type DiscountType,
  type Shop,
} from '~/lib/api/shop';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  Field,
  LoadError,
  Section,
  Segmented,
  SkeletonCard,
  Spinner,
  Switch,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { AdminExtensionSlot } from '~/components/workbench/admin-extensions';
import {
  apiErrorMessage,
  DISCOUNT_CODE_RE,
  discountValueLabel,
  formatMinor,
  parseCount,
  parseMoney,
  percentToBps,
} from './shop-model';

export function CollectionsView({ appId, shop }: { appId: string; shop: Shop }) {
  return (
    <>
      <CollectionsSection appId={appId} />
      <DiscountsSection appId={appId} shop={shop} />
    </>
  );
}

// ─── collections ───

function ruleLabel(c: Collection): string {
  if (!c.rule) {
    return 'Manual';
  }

  return [c.rule.tag && `Tag “${c.rule.tag}”`, c.rule.productType && `Type “${c.rule.productType}”`]
    .filter(Boolean)
    .join(' and ');
}

function CollectionsSection({ appId }: { appId: string }) {
  const collections = useResource(() => apiListCollections(appId), appId);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const addButton = !creating && (
    <button onClick={() => setCreating(true)} className={BTN_GHOST}>
      <div className="i-ph:plus text-[13px]" />
      New
    </button>
  );

  return (
    <Section title="Collections" icon="i-ph:folders" aside={addButton}>
      {creating && (
        <CollectionForm
          appId={appId}
          onDone={(saved) => {
            setCreating(false);

            if (saved) {
              void collections.reload();
            }
          }}
        />
      )}

      {collections.loading && !collections.data ? (
        <SkeletonCard />
      ) : collections.error ? (
        <LoadError message="Could not load collections." onRetry={() => void collections.reload()} />
      ) : !collections.data?.length ? (
        !creating && (
          <div className={classNames(CARD, 'px-4 py-5 text-center')}>
            <p className="text-[12px] text-muted">
              No collections yet. Group products by hand, or by a tag or product type.
            </p>
          </div>
        )
      ) : (
        <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
          {collections.data.map((c) =>
            editingId === c.id ? (
              <div key={c.id} className="space-y-3 p-2">
                <CollectionForm
                  appId={appId}
                  collection={c}
                  onDone={(saved) => {
                    setEditingId(null);

                    if (saved) {
                      void collections.reload();
                    }
                  }}
                />
                <AdminExtensionSlot appId={appId} target="shop.collection.details" resourceId={c.id} />
              </div>
            ) : (
              <div key={c.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="truncate text-[13px] text-primary">{c.title}</div>
                  <div className="font-geist-mono text-[11px] text-muted">{c.slug}</div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-[11px] text-secondary">{ruleLabel(c)}</span>
                  <button onClick={() => setEditingId(c.id)} className={BTN_GHOST} aria-label={`Edit ${c.title}`}>
                    <div className="i-ph:pencil-simple text-[13px]" />
                    Edit
                  </button>
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </Section>
  );
}

function CollectionForm({
  appId,
  collection,
  onDone,
}: {
  appId: string;
  /** Set to edit an existing collection; omitted to create one. */
  collection?: Collection;
  onDone: (saved: boolean) => void;
}) {
  const editing = Boolean(collection);
  const [title, setTitle] = useState(collection?.title ?? '');
  const [kind, setKind] = useState<'manual' | 'rule'>(collection?.rule ? 'rule' : 'manual');
  const [tag, setTag] = useState(collection?.rule?.tag ?? '');
  const [productType, setProductType] = useState(collection?.rule?.productType ?? '');
  const [picked, setPicked] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const products = useResource(kind === 'manual' ? () => apiListProducts(appId) : null, `${appId}:${kind}`);
  // Current manual members, so editing starts from what's already in the collection.
  const members = useResource(
    collection && !collection.rule ? () => apiCollectionProductIds(appId, collection.id) : null,
    `${appId}:${collection?.id ?? 'new'}:members`,
  );

  useEffect(() => {
    if (members.data) {
      setPicked(members.data);
    }
  }, [members.data]);

  const remove = async () => {
    if (!collection) {
      return;
    }

    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await apiDeleteCollection(appId, collection.id);
      onDone(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete this collection.'));
      setSaving(false);
    }
  };

  const toggle = (id: string) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Give the collection a title.');
      return;
    }

    if (kind === 'rule' && !tag.trim() && !productType.trim()) {
      setError('A rule needs a tag, a product type, or both.');
      return;
    }

    setSaving(true);

    try {
      const input = {
        title: title.trim(),
        ...(kind === 'rule'
          ? {
              rule: {
                ...(tag.trim() ? { tag: tag.trim() } : {}),
                ...(productType.trim() ? { productType: productType.trim() } : {}),
              },
            }
          : { rule: null, productIds: picked }),
      };

      if (collection) {
        await apiUpdateCollection(appId, collection.id, input);
      } else {
        await apiCreateCollection(appId, input);
      }

      onDone(true);
    } catch (err) {
      setError(apiErrorMessage(err, editing ? 'Could not save this collection.' : 'Could not create this collection.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className={classNames(CARD, 'space-y-3 p-4')}>
      <Field label="Title">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Summer mugs"
          className="ui-input"
        />
      </Field>

      <div className="space-y-1.5">
        <span className="block text-[11px] text-secondary">Products</span>
        <Segmented
          label="Collection type"
          value={kind}
          options={[
            ['manual', 'Pick by hand'],
            ['rule', 'Match a rule'],
          ]}
          onChange={setKind}
        />
      </div>

      {kind === 'rule' ? (
        <>
          <div className="flex gap-2">
            <div className="min-w-0 flex-1">
              <Field label="Tag">
                <input
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  placeholder="summer"
                  className="ui-input !py-2"
                />
              </Field>
            </div>
            <div className="min-w-0 flex-1">
              <Field label="Product type">
                <input
                  value={productType}
                  onChange={(e) => setProductType(e.target.value)}
                  placeholder="Mug"
                  className="ui-input !py-2"
                />
              </Field>
            </div>
          </div>
          <p className="text-[11px] leading-relaxed text-muted">
            Products that match (both, when you set both) join automatically, including ones you add later.
          </p>
        </>
      ) : (products.loading && !products.data) || (members.loading && !members.data) ? (
        <SkeletonCard />
      ) : members.error ? (
        <LoadError message="Could not load this collection's products." onRetry={() => void members.reload()} />
      ) : products.error ? (
        <LoadError message="Could not load products." onRetry={() => void products.reload()} />
      ) : !products.data?.length ? (
        <p className="text-[12px] text-muted">Add products first, then group them here.</p>
      ) : (
        <div className="max-h-48 divide-y divide-hairline overflow-y-auto rounded-lg border border-hairline bg-sunken">
          {products.data.map((d) => (
            <label key={d.product.id} className="flex cursor-pointer items-center gap-2.5 px-3 py-2">
              <input
                type="checkbox"
                checked={picked.includes(d.product.id)}
                onChange={() => toggle(d.product.id)}
                className="accent-accent"
              />
              <span className="truncate text-[12px] text-primary">{d.product.title}</span>
            </label>
          ))}
        </div>
      )}

      {error && <ErrorBanner message={error} />}

      <div className="flex items-center gap-2">
        <button type="submit" disabled={saving} className={BTN_SECONDARY}>
          {saving && <Spinner />}
          {editing ? (saving ? 'Saving…' : 'Save collection') : saving ? 'Creating…' : 'Create collection'}
        </button>
        <button type="button" onClick={() => onDone(false)} disabled={saving} className={BTN_GHOST}>
          Cancel
        </button>
        {editing && (
          <button
            type="button"
            onClick={() => void remove()}
            disabled={saving}
            className={classNames(BTN_GHOST, 'ml-auto', confirmDelete && 'text-danger')}
          >
            <div className="i-ph:trash text-[13px]" />
            {confirmDelete ? 'Confirm delete' : 'Delete'}
          </button>
        )}
      </div>
      {confirmDelete && (
        <p className="text-[11px] text-muted">Deleting removes the collection only; its products stay in the shop.</p>
      )}
    </form>
  );
}

// ─── discounts ───

function DiscountsSection({ appId, shop }: { appId: string; shop: Shop }) {
  const discounts = useResource(() => apiListDiscounts(appId), appId);
  const [creating, setCreating] = useState(false);
  const discountsOn = shop.modules.includes('discounts');

  const addButton = !creating && (
    <button onClick={() => setCreating(true)} className={BTN_GHOST}>
      <div className="i-ph:plus text-[13px]" />
      New code
    </button>
  );

  return (
    <Section title="Discount codes" icon="i-ph:ticket" aside={addButton}>
      {!discountsOn && (
        <p className="text-[11px] leading-relaxed text-warning">
          The Discounts module is off, so checkout ignores codes. Turn it on in Settings.
        </p>
      )}

      {creating && (
        <DiscountForm
          appId={appId}
          currency={shop.currency}
          onDone={(saved) => {
            setCreating(false);

            if (saved) {
              void discounts.reload();
            }
          }}
        />
      )}

      {discounts.loading && !discounts.data ? (
        <SkeletonCard />
      ) : discounts.error ? (
        <LoadError message="Could not load discount codes." onRetry={() => void discounts.reload()} />
      ) : discounts.data?.length ? (
        <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
          {discounts.data.map((d) => (
            <DiscountRow
              key={d.id}
              appId={appId}
              discount={d}
              currency={shop.currency}
              onChanged={(next) => discounts.setData((prev) => (prev ?? []).map((x) => (x.id === next.id ? next : x)))}
              onDeleted={() => discounts.setData((prev) => (prev ?? []).filter((x) => x.id !== d.id))}
            />
          ))}
        </div>
      ) : (
        !creating && (
          <div className={classNames(CARD, 'px-4 py-5 text-center')}>
            <p className="text-[12px] text-muted">No discount codes yet. Buyers enter them at checkout.</p>
          </div>
        )
      )}
    </Section>
  );
}

function DiscountRow({
  appId,
  discount,
  currency,
  onChanged,
  onDeleted,
}: {
  appId: string;
  discount: Discount;
  currency: string;
  onChanged: (next: Discount) => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A used code is referenced by orders, so the server only deletes unused ones.
  const deletable = discount.usedCount === 0;

  const remove = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await apiDeleteDiscount(appId, discount.code);
      onDeleted();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete this code.'));
      setConfirmDelete(false);
      setBusy(false);
    }
  };

  const toggle = async (active: boolean) => {
    setBusy(true);
    setError(null);

    try {
      onChanged(await apiUpdateDiscount(appId, discount.code, { active }));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update this code.'));
    } finally {
      setBusy(false);
    }
  };

  const limits = [
    discount.minSubtotal ? `over ${formatMinor(discount.minSubtotal, currency)}` : '',
    discount.usageLimit ? `${discount.usedCount}/${discount.usageLimit} used` : `${discount.usedCount} used`,
  ].filter(Boolean);

  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-geist-mono text-[13px] text-primary">{discount.code}</div>
          <div className="text-[11px] text-muted">
            {discountValueLabel(discount, currency)} · {limits.join(' · ')}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {deletable && (
            <button
              onClick={() => void remove()}
              disabled={busy}
              className={classNames(BTN_GHOST, confirmDelete && 'text-danger')}
              aria-label={`Delete ${discount.code}`}
            >
              <div className="i-ph:trash text-[13px]" />
              {confirmDelete ? 'Confirm' : 'Delete'}
            </button>
          )}
          <Switch checked={discount.active} onChange={(v) => void toggle(v)} disabled={busy} label="Active" />
        </div>
      </div>
      {error && (
        <div className="mt-2">
          <ErrorBanner message={error} />
        </div>
      )}
    </div>
  );
}

function DiscountForm({
  appId,
  currency,
  onDone,
}: {
  appId: string;
  currency: string;
  onDone: (saved: Discount | null) => void;
}) {
  const [code, setCode] = useState('');
  const [type, setType] = useState<DiscountType>('percent');
  const [value, setValue] = useState('');
  const [minSubtotal, setMinSubtotal] = useState('');
  const [usageLimit, setUsageLimit] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const nextCode = code.trim().toUpperCase();

    if (!DISCOUNT_CODE_RE.test(nextCode)) {
      setError('A code is 2–40 letters, digits, - or _.');
      return;
    }

    let amount = 0;

    if (type === 'percent') {
      const bps = percentToBps(value);

      if (bps === null) {
        setError('Enter a percentage from 0.01 to 100.');
        return;
      }

      amount = bps;
    } else if (type === 'fixed') {
      const minor = parseMoney(value, currency);

      if (minor === null) {
        setError(`Enter an amount in ${currency}, e.g. 100.`);
        return;
      }

      amount = minor;
    }

    const min = minSubtotal.trim() ? parseMoney(minSubtotal, currency) : null;

    if (minSubtotal.trim() && min === null) {
      setError('Minimum order is an amount, or leave it blank.');
      return;
    }

    const limit = usageLimit.trim() ? parseCount(usageLimit) : null;

    if (usageLimit.trim() && (limit === null || limit < 1)) {
      setError('Usage limit is a whole number of 1 or more, or blank for unlimited.');
      return;
    }

    setSaving(true);

    try {
      onDone(
        await apiCreateDiscount(appId, {
          code: nextCode,
          type,
          value: amount,
          minSubtotal: min,
          usageLimit: limit,
        }),
      );
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create this code.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className={classNames(CARD, 'space-y-3 p-4')}>
      <Field label="Code">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="WELCOME10"
          spellCheck={false}
          className="ui-input-mono uppercase"
        />
      </Field>

      <div className="space-y-1.5">
        <span className="block text-[11px] text-secondary">Type</span>
        <Segmented
          label="Discount type"
          value={type}
          options={[
            ['percent', 'Percent'],
            ['fixed', 'Fixed amount'],
            ['free_shipping', 'Free shipping'],
          ]}
          onChange={(t) => {
            setType(t);
            setValue('');
          }}
        />
      </div>

      {type !== 'free_shipping' && (
        <Field label={type === 'percent' ? 'Percent off' : `Amount off (${currency})`}>
          <div className="flex items-center gap-2">
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              inputMode="decimal"
              placeholder={type === 'percent' ? '10' : '100'}
              className="ui-input-mono !w-28"
            />
            {type === 'percent' && <span className="text-[12px] text-muted">%</span>}
          </div>
        </Field>
      )}

      <div className="flex gap-2">
        <div className="min-w-0 flex-1">
          <Field label={`Minimum order (${currency})`} hint="Optional.">
            <input
              value={minSubtotal}
              onChange={(e) => setMinSubtotal(e.target.value)}
              inputMode="decimal"
              placeholder="None"
              className="ui-input-mono !py-2"
            />
          </Field>
        </div>
        <div className="min-w-0 flex-1">
          <Field label="Usage limit" hint="Blank = unlimited.">
            <input
              value={usageLimit}
              onChange={(e) => setUsageLimit(e.target.value)}
              inputMode="numeric"
              placeholder="Unlimited"
              className="ui-input-mono !py-2"
            />
          </Field>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="flex items-center gap-2">
        <button type="submit" disabled={saving} className={BTN_SECONDARY}>
          {saving && <Spinner />}
          {saving ? 'Creating…' : 'Create code'}
        </button>
        <button type="button" onClick={() => onDone(null)} disabled={saving} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}
